import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faTrash } from '@fortawesome/free-solid-svg-icons';

const ConversationHistoryContainer = styled.div`
  padding: var(--space-4);
  background-color: var(--surface-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color-light);
`;

const ConversationList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const ConversationItem = styled.li`
  padding: var(--space-3) var(--space-4);
  cursor: pointer;
  background-color: ${props => props.isActive ? 'var(--primary-100)' : 'transparent'};
  color: ${props => props.isActive ? 'var(--primary-700)' : 'var(--text-color)'};
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${props => props.isActive ? 'var(--primary-200)' : 'transparent'};
  font-family: var(--font-heading-regular);
  font-size: var(--text-sm);

  &:hover {
    background-color: var(--primary-50);
    border-color: var(--primary-200);
    transform: translateY(-1px);
  }
`;

const ConversationInfo = styled.div`
  flex-grow: 1;
  margin-left: var(--space-2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ConversationTitle = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: var(--font-heading-medium);
  font-weight: 400;
`;

const ConversationDate = styled.span`
  font-size: var(--text-xs);
  color: var(--text-secondary);
  margin-top: var(--space-1);
  font-family: var(--font-family);
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  opacity: 0.6;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: var(--space-1);
  border-radius: var(--radius-sm);

  &:hover {
    opacity: 1;
    background-color: var(--error-50);
    color: var(--error-600);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-600);
  margin-top: var(--space-2);
  padding: var(--space-2);
  background-color: var(--error-50);
  border-radius: var(--radius-lg);
  border: 1px solid var(--error-200);
  font-family: var(--font-heading-regular);
  font-size: var(--text-xs);
  font-weight: 500;
`;

function ConversationHistory({ conversations, onSelectConversation, currentConversationId, onConversationDeleted }) {
  const [deleteError, setDeleteError] = useState(null);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      // Less than 24 hours - show time
      return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      // Yesterday
      return 'אתמול';
    } else if (diffInHours < 168) {
      // Less than a week - show day name
      return date.toLocaleDateString('he-IL', { weekday: 'long' });
    } else {
      // Show date
      return date.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' });
    }
  };

  const handleDeleteConversation = async (e, conversationId) => {
    e.stopPropagation();
    setDeleteError(null);
    try {
      // Call the parent's delete handler
      await onConversationDeleted(conversationId);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setDeleteError('Failed to delete conversation. Please try again.');
    }
  };

  return (
    <ConversationHistoryContainer>
      <h3 style={{
        marginBottom: 'var(--space-4)',
        fontSize: 'var(--text-lg)',
        fontWeight: '400',
        color: 'var(--text-color)',
        fontFamily: 'var(--font-heading-medium)'
      }}>Conversation History</h3>
      {deleteError && <ErrorMessage>{deleteError}</ErrorMessage>}
      <ConversationList>
        {conversations.map((conversation) => (
          <ConversationItem
            key={conversation.id}
            onClick={() => onSelectConversation(conversation)}
            isActive={currentConversationId === conversation.id}
          >
            <FontAwesomeIcon icon={faComments} />
            <ConversationInfo>
              <ConversationTitle>{conversation.title}</ConversationTitle>
              <ConversationDate>{formatDate(conversation.updatedAt || conversation.createdAt)}</ConversationDate>
            </ConversationInfo>
            <DeleteButton onClick={(e) => handleDeleteConversation(e, conversation.id)}>
              <FontAwesomeIcon icon={faTrash} />
            </DeleteButton>
          </ConversationItem>
        ))}
      </ConversationList>
    </ConversationHistoryContainer>
  );
}

export default ConversationHistory;